import React, { useState, useEffect, useRef } from "react";
import { 
  Box, Typography, Grid, TextField, Paper, Checkbox, 
  FormControlLabel, Avatar, Button, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack
} from "@mui/material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { 
  CloudUpload as CloudUploadIcon, 
  Delete as DeleteIcon,
  CameraAlt as CameraAltIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { storage } from "../../../firebase"; // Ensure your firebase config is exported

function MotherInfo({ form, handleChange }) {
  const [sameAddress, setSameAddress] = useState(false);
  const [sameAsFather, setSameAsFather] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Webcam States and Refs
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (sameAsFather) {
      handleChange("mother_currentAddress", form.father_currentAddress || "");
      handleChange("mother_currentPin", form.father_currentPin || "");
      handleChange("mother_permanentAddress", form.father_permanentAddress || "");
      handleChange("mother_permanentPin", form.father_permanentPin || "");
      handleChange("mother_officeAddress", form.father_officeAddress || "");
      handleChange("mother_officePin", form.father_officePin || "");
    }
  }, [
    sameAsFather,
    form.father_currentAddress,
    form.father_currentPin,
    form.father_permanentAddress,
    form.father_permanentPin,
    form.father_officeAddress,
    form.father_officePin
  ]);

  useEffect(() => {
    if (sameAddress) {
      handleChange("mother_permanentAddress", form.mother_currentAddress || "");
      handleChange("mother_permanentPin", form.mother_currentPin || "");
    }
  }, [sameAddress, form.mother_currentAddress, form.mother_currentPin]);

  // --- REUSABLE FIREBASE UPLOAD LOGIC ---
  const uploadPhotoToFirebase = async (fileBlob, fileName) => {
    setUploading(true);
    try {
      const storageRef = ref(storage, `mother_photos/${Date.now()}_${fileName}`);
      await uploadBytes(storageRef, fileBlob);
      const downloadURL = await getDownloadURL(storageRef);
      handleChange("mother_photo", downloadURL);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // --- FILE UPLOAD HANDLER ---
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadPhotoToFirebase(file, file.name);
    }
  };

  // --- WEBCAM LOGIC ---
  const startCamera = async () => {
    setCameraOpen(true);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
    } catch (error) {
      console.error("Camera access denied:", error);
      alert("Camera access denied or unavailable.");
      setCameraOpen(false);
    }
  };

  // Attach stream to video element when it becomes available
  useEffect(() => {
    if (cameraOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [cameraOpen, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setCameraOpen(false);
    setStream(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to Blob and upload
      canvas.toBlob((blob) => {
        if (blob) {
          stopCamera();
          uploadPhotoToFirebase(blob, `webcam_capture.jpg`);
        }
      }, "image/jpeg", 0.9);
    }
  };

  // Cleanup camera stream if component unmounts
  useEffect(() => {
    return () => stopCamera();
  }, []);

  return (
    <Box display="flex" justifyContent="center" bgcolor="#f4f6f8">
      <Paper sx={{ p: 1, width: "100%", maxWidth: "1200px" }}>
        
        <Grid className="row" item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sameAsFather}
                onChange={(e) => setSameAsFather(e.target.checked)}
              />
            }
            label="Same as Father Address"
          />
        </Grid>

        {/* HEADER */}
        <Box sx={{ background: "teal", color: "#fff", p: 1, mb: 3, borderRadius: 1 }}>
          <Typography fontWeight="bold">
            4) Mother's Information (Please fill in BLOCK CAPITAL LETTERS ONLY)
          </Typography>
        </Box>

        <Grid className="row">
          {/* LEFT SIDE: FORM FIELDS */}
          <Grid className="col-sm-9" item xs={12} md={9}>
            <Grid className="row" spacing={2}>
              <Grid className="col-sm-3 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  label="Name (Start's with Surname)"
                  size="small"
                  value={form.mother_name || ""}
                  onChange={(e) => handleChange("mother_name", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={9}>
                <TextField
                  fullWidth
                  label="Current Residential Address"
                  size="small"
                  value={form.mother_currentAddress || ""}
                  onChange={(e) => handleChange("mother_currentAddress", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={3}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  size="small"
                  value={form.mother_currentPin || ""}
                  onChange={(e) => handleChange("mother_currentPin", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={9}>
                <TextField
                  fullWidth
                  label="Permanent Residential Address"
                  size="small"
                  value={form.mother_permanentAddress || ""}
                  onChange={(e) => handleChange("mother_permanentAddress", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={3}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  size="small"
                  value={form.mother_permanentPin || ""}
                  onChange={(e) => handleChange("mother_permanentPin", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={6}>
                <TextField
                  fullWidth
                  label="PAN No."
                  size="small"
                  value={form.mother_pan || ""}
                  onChange={(e) => handleChange("mother_pan", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={6}>
                <TextField
                  fullWidth
                  label="Adhar No."
                  size="small"
                  value={form.mother_adhar || ""}
                  onChange={(e) => handleChange("mother_adhar", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={6}>
                <TextField
                  fullWidth
                  label="Organization"
                  size="small"
                  value={form.mother_organization || ""}
                  onChange={(e) => handleChange("mother_organization", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={6}>
                <TextField
                  fullWidth
                  label="Qualification"
                  size="small"
                  value={form.mother_qualification || ""}
                  onChange={(e) => handleChange("mother_qualification", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={6}>
                <TextField
                  fullWidth
                  label="Occupation"
                  size="small"
                  value={form.mother_occupation || ""}
                  onChange={(e) => handleChange("mother_occupation", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={6}>
                <TextField
                  fullWidth
                  label="Designation"
                  size="small"
                  value={form.mother_designation || ""}
                  onChange={(e) => handleChange("mother_designation", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={9}>
                <TextField
                  fullWidth
                  label="Office Address"
                  size="small"
                  value={form.mother_officeAddress || ""}
                  onChange={(e) => handleChange("mother_officeAddress", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={3}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  size="small"
                  value={form.mother_officePin || ""}
                  onChange={(e) => handleChange("mother_officePin", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  label="Email ID"
                  size="small"
                  value={form.mother_email || ""}
                  onChange={(e) => handleChange("mother_email", e.target.value)}
                />
              </Grid>

              {/* Title changed from Mobile 1 to Primary No */}
              <Grid className="col-sm-3 mb-3" item xs={4}>
                <TextField
                  fullWidth
                  label="Primary No"
                  size="small"
                  value={form.mother_mobile1 || ""}
                  onChange={(e) => handleChange("mother_mobile1", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={4}>
                <TextField
                  fullWidth
                  label="Mobile 2"
                  size="small"
                  value={form.mother_mobile2 || ""}
                  onChange={(e) => handleChange("mother_mobile2", e.target.value)}
                />
              </Grid>

              <Grid className="col-sm-3 mb-3" item xs={4}>
                <TextField
                  fullWidth
                  label="Landline"
                  size="small"
                  value={form.mother_landline || ""}
                  onChange={(e) => handleChange("mother_landline", e.target.value)}
                />
              </Grid>

            </Grid>
          </Grid>

          {/* RIGHT SIDE: PHOTO UPLOAD */}
          <Grid className="col-sm-3" item xs={12} md={3}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{
                border: "1px dashed #ccc",
                p: 2,
                borderRadius: 2,
                height: "100%",
                minHeight: "280px",
                bgcolor: "#fafafa"
              }}
            >
              <Typography variant="caption" sx={{ mb: 1, fontWeight: "bold" }}>MOTHER'S PHOTO</Typography>

              <Avatar
                variant="rounded"
                src={form.mother_photo || ""}
                sx={{ width: 150, height: 180, mb: 2, border: "1px solid #ddd" }}
              >
                {uploading ? <CircularProgress size={24} /> : "No Image"}
              </Avatar>

              {/* Upload Controls */}
              <Stack spacing={1} width="100%" alignItems="center">
                <Button
                  variant="contained"
                  component="label"
                  size="small"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#00695c" }, maxWidth: 180 }}
                >
                  {uploading ? "Uploading..." : "Upload File"}
                  <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<CameraAltIcon />}
                  disabled={uploading}
                  onClick={startCamera}
                  sx={{ color: "teal", borderColor: "teal", "&:hover": { borderColor: "#00695c" }, maxWidth: 180 }}
                >
                  Capture Photo
                </Button>
              </Stack>

              {form.mother_photo && (
                <IconButton
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => handleChange("mother_photo", "")}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* WEBCAM DIALOG */}
      <Dialog open={cameraOpen} onClose={stopCamera} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'teal', color: 'white' }}>
          <Typography variant="h6">Capture Photo</Typography>
          <IconButton
            aria-label="close"
            onClick={stopCamera}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#000' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", maxWidth: "480px", borderRadius: "8px", transform: "scaleX(-1)" }} // Mirrors the webcam for user comfort
          />
          {/* Hidden Canvas for extracting the frame */}
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<CameraAltIcon />} 
            onClick={capturePhoto}
            sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#00695c" }, px: 4, py: 1 }}
          >
            Snap & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MotherInfo;